"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import {
  ArrowLeft,
  BarChart3,
  Building2,
  ChevronRight,
  CircleDollarSign,
  Cpu,
  Image as ImageIcon,
  KeyRound,
  LogOut,
  Menu,
  MessageSquare,
  Server,
  ShieldAlert,
  ShieldCheck,
  Users,
  X,
} from "lucide-react";
import { useSession } from "@/components/session-provider";
import { ErrorNotice, IconButton } from "@/components/ui";
import { errorMessage } from "@/lib/api";

export function StaffShell({ children }: { children: React.ReactNode }) {
  const session = useSession();
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    if (!open) return;
    const desktop = window.matchMedia("(min-width: 801px)");
    const resize = () => {
      if (desktop.matches) setOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
        document.getElementById("navigation-toggle")?.focus();
      }
    };
    desktop.addEventListener("change", resize);
    window.addEventListener("keydown", escape);
    resize();
    return () => {
      desktop.removeEventListener("change", resize);
      window.removeEventListener("keydown", escape);
    };
  }, [open]);
  if (!session.user) return null;
  const links = [
    { href: "/staff", label: "Overview", icon: BarChart3, permission: "platform.read", exact: true },
    { href: "/staff/workspaces", label: "Workspaces", icon: Building2, permission: "platform.read" },
    { href: "/staff/accounts", label: "Accounts", icon: Users, permission: "platform.read" },
    { href: "/staff/access", label: "Staff access", icon: KeyRound, permission: "platform.read" },
    { href: "/staff/servers", label: "Servers", icon: Server, permission: "platform.read" },
    { href: "/staff/discord", label: "Discord", icon: MessageSquare, permission: "platform.read" },
    { href: "/staff/security", label: "Security audit", icon: ShieldAlert, permission: "platform.read" },
    { href: "/staff/budgets", label: "Budgets", icon: CircleDollarSign, permission: "platform.read" },
    { href: "/staff/cdn", label: "CDN", icon: ImageIcon, permission: "platform.cdn.manage" },
  ].filter((link) => session.can(link.permission));
  if (!links.length)
    return (
      <main className="boot-screen">
        <ShieldAlert size={28} />
        <span>Staff access required</span>
        <Link href="/" className="button">
          <ArrowLeft size={16} />
          Back to workspace
        </Link>
      </main>
    );
  const section =
    links.find((link) => link.exact ? pathname === link.href : pathname.startsWith(link.href))?.label || "Overview";
  return (
    <div className="panel-shell staff-area">
      {open && (
        <button
          className="nav-backdrop"
          aria-label="Close navigation"
          onClick={() => setOpen(false)}
        />
      )}
      <aside
        id="panel-navigation"
        className={`sidebar ${open ? "sidebar-open" : ""}`}
      >
        <Link href={links[0].href} className="brand" onClick={() => setOpen(false)}>
          <ShieldCheck size={24} />
          <span>
            Velnqo<span className="brand-suffix">Staff</span>
          </span>
        </Link>
        <IconButton
          className="mobile-nav-close"
          label="Close navigation"
          onClick={() => setOpen(false)}
        >
          <X size={18} />
        </IconButton>
        <nav aria-label="Staff navigation">
          <Link
            href="/"
            onClick={() => setOpen(false)}
            className="nav-item nav-switch"
          >
            <ArrowLeft size={18} />
            Back to workspace
          </Link>
          <div className="nav-label nav-label-admin">Business operations</div>
          {links.map(({ href, label, icon: Icon }) => (
            <Link
              key={href}
              href={href}
              onClick={() => setOpen(false)}
              aria-current={pathname === href || (href !== "/staff" && pathname.startsWith(href)) ? "page" : undefined}
              className={`nav-item ${pathname === href || (href !== "/staff" && pathname.startsWith(href)) ? "nav-active" : ""}`}
            >
              <Icon size={18} />
              {label}
            </Link>
          ))}
        </nav>
        <div className="sidebar-bottom">
          <div className="account">
            <Link
              href="/settings/account"
              className="account-link"
              onClick={() => setOpen(false)}
              aria-current={pathname === "/settings/account" ? "page" : undefined}
            >
              <span className="avatar">
                {(session.user.name || session.user.username)
                  .slice(0, 2)
                  .toUpperCase()}
              </span>
              <div>
                <strong>{session.user.name || session.user.username}</strong>
                <small>
                  {session.can("platform.read") ? "Platform staff" : "Staff"}
                </small>
              </div>
            </Link>
            <IconButton
              label="Sign out"
              onClick={async () => {
                try {
                  await session.logout();
                } catch (error) {
                  setError(errorMessage(error));
                }
              }}
            >
              <LogOut size={16} />
            </IconButton>
          </div>
        </div>
      </aside>
      <div className="panel-main" inert={open}>
        <header className="topbar">
          <IconButton
            className="mobile-nav-toggle"
            id="navigation-toggle"
            aria-expanded={open}
            aria-controls="panel-navigation"
            label="Open navigation"
            onClick={() => setOpen(true)}
          >
            <Menu size={20} />
          </IconButton>
          <div className="breadcrumbs">
            <Cpu size={16} />
            <span>Administration</span>
            <ChevronRight size={14} />
            <strong>{section}</strong>
          </div>
          <span className="workspace-label">
            <span className="connection-dot" />
            Velnqo Staff
          </span>
        </header>
        <main className="page-content">
          <ErrorNotice message={error} />
          {children}
        </main>
      </div>
    </div>
  );
}
